import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ConfirmModalLoaderPayload } from "@ispm/layout/classes/confirm-modal-loader-payload";
import { modalResponse } from "@ispm/layout/constants/modal-responses.enum";
import { ModalLoaderNgjsService } from "@ispm/layout/services/modal-loader-ngjs.service";
import { IdynamicFormItem } from "@ispm/shared/dynamic-form/interfaces/IdynamicFormItem";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { get, isEmpty } from "lodash";
import { filter, map } from "rxjs/operators";
import { Widget } from "../../decorator/widget.decorator";
import { IWidgetItem } from "../../interfaces/widget-item.interface";
import { AdditionalConfigurationService } from '@ispm/shared/services/additionalConfiguration.service';
import { Subject, Observable } from "rxjs";
import { NotificationMessageService } from "@ispm/core/components/customise-notification/notifications-message.service";
import { ActivatedRoute } from '@angular/router';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Component({
  selector: 'ispm-app-config-general-info-tab-widget',
  templateUrl: './app-config-general-info-tab-widget.component.html',
  styleUrls: ['./app-config-general-info-tab-widget.component.scss']
})

@Widget('AppConfigGeneralInfoWidget')
export class AppConfigGeneralInfoComponent implements OnInit, IWidgetItem {

   appId: string = '';
   organizationId: string = '';
   widgetPayload: any;
   enableEditButton: any = false;
   controls: any;
   isEditMode: boolean = false;
   manualTags: any;
   manualTagsFormConfig = [];
   payloadBinding = [];
   formConfig: any;
   disableEditButton: boolean = true;
   record: any = {};
   destroyed$: Subject<any> = new Subject();

  public tagsData: Observable<any>;

  widgetData: any;
  widgetUpdateValueCallback?: (value: any) => void;
  forceUpdate?: (state: boolean) => void;
  addWidgetFormControl?: (config: IdynamicFormItem<any>) => void;
  widgetFormInstance?: FormGroup;
  widgetDisabledStatus?: boolean;
  widgetSubmitStatusValueCallback?: (value: boolean) => void;

  constructor(private _changeDetector: ChangeDetectorRef,
    private _modalLoaderService: ModalLoaderNgjsService,
    private _translate: TranslateService,
    private store: Store<any>,
    private tagsService: AdditionalConfigurationService,
    private notificationMessageService: NotificationMessageService,
    private routerManagerService: RouterManagerService) {

    this.appId = this.routerManagerService.parameters.id;
    this.store.select('users', 'currentUser', 'organization')
    .takeUntil(this.destroyed$.asObservable())
    .subscribe( data => this.organizationId = data);
  }

  ngOnInit(): void {

    this.store.select('users', 'organizationConfiguration', 'applications').pipe(
      filter(applications => !!applications),
      map( (applications) =>  {
       let value = applications.find(app => app.appId === this.appId)
        if(value) this.disableEditButton = false;
      }))
      .takeUntil(this.destroyed$.asObservable())
      .subscribe();

      let queryString = `type=tags&context=[` + this.organizationId + `,` + this.appId + `]`;
      this.tagsData = this.tagsService.getAdditionalConfiguration(queryString)
      .map( data => {
        this.record = data[0];
        return  get(this.record, 'payload',{});
      });
  }

  set widgetFormInstanceSubmitted(widgetFormInstanceSubmitted: boolean) {
    if (widgetFormInstanceSubmitted) {
      this.submitForm();
    }
  }

  /**
   * set General Info Tab widget config
   * @param widgetConfig
   */
  set widgetConfig(widgetConfig: IdynamicFormItem) {
    this.widgetPayload = get(widgetConfig, 'widgetPayload', { });
    this.enableEditButton = get(widgetConfig.widgetPayload, 'enableEditButton', false);
    this.controls = get(widgetConfig.widgetPayload, 'controls', []);

    this.payloadBinding =  get(widgetConfig.widgetPayload, 'payloadBinding', []);
    this.formConfig = get(widgetConfig.widgetPayload, 'formConfig', []);
  }

   /**
   * Triggered when edit, submit, cancel button is clicked
   * @param type
   */
  buttonClick(type:string) {
    switch (type) {
      case 'submit':
        this.widgetFormInstanceSubmitted = true;
        break;
      case 'edit':
        this.isEditMode = true;
        this._changeDetector.markForCheck();
        break;
      case 'cancel':
        this.cancelClick();
        break;
    }
  }

  /**
   * Setting PopUp configuration needed on click of cancel button
   */
  cancelClick() {
    this._modalLoaderService.popupModal('confirm',
      ConfirmModalLoaderPayload.builder()
        .setTitle(this._translate.instant('DISCARD_DATA') || 'Discard Data')
        .setContent(this._translate.instant('ADMIN_UN_SAVED_CHANGES') || 'Canceling will return without saving your changes. Are you sure?')
        .setDeclineButtonText(this._translate.instant('No') || 'No')
        .setApproveButtonText(this._translate.instant('Yes') || 'Yes'),
      'INFO').subscribe(response => {
      if (get(response, 'payload') === modalResponse.approve) {
        //this.resetData();
        this.isEditMode = false;
        this._changeDetector.markForCheck();
      }
    });
  }

  /**
   * Triggered if there is any change in form data
   * @param event
   */
  onUpdateData($event) {
    if ($event.updated.includes('tags')) {
      this.manualTags = get($event.form, 'tags');
    }
  }

  /**
   * form data is updated based on if the data is already exists or not
   * @param event
   */
  submitForm() {

    this.prepareManualTagsData()

    // Based on record, the entry is either updated or created for AdditionalConfiguration
    if(!isEmpty(this.record)){

      this.record.payload.tags = this.manualTags;

      this.tagsService.updateAdditionalConfiguration(this.record)
      .takeUntil(this.destroyed$.asObservable())
      .subscribe(
        updateResponse => {
          if (this.isEditMode) {
            this.isEditMode = false;
            this._changeDetector.markForCheck();
          }
          this.record = updateResponse;
          this.notificationMessageService.success('Application configuration submission success');
        },
        error => {
          this.widgetSubmitStatusValueCallback.apply(this, [false]);
          this.notificationMessageService.error('Application configuration submission error');
          }
        );
    }
    else{
      let data =  this.getCreateCallPayload();

      this.tagsService.createAdditionalConfiguration(data)
      .takeUntil(this.destroyed$.asObservable())
      .subscribe(
        createResponse => {
          if (this.isEditMode) {
            this.isEditMode = false;
            this._changeDetector.markForCheck();
          }
          this.record = createResponse;
          this.notificationMessageService.success('Application configuration submission success');
      },
      error => {
        this.widgetSubmitStatusValueCallback.apply(this, [false]);
        this.notificationMessageService.error('Application configuration submission error');
        }
      );

    }
  }

  /**
   *  adding isAdminTag attribute to every Tags
   */
  prepareManualTagsData(){
    this.manualTags.forEach(element => {
      element['isAdminTag'] = true;
    });
  }

  /**
   * Preparing the payload data to create new AdditionalConfiguration entry
   */
  getCreateCallPayload(){

    let data = {};

    data['name'] = this.appId + '-TAGS-' + this.organizationId;
    data['context'] = [
      {
        "reference": this.organizationId,
        "resourceType": "Organization"
      },
      {
          "reference": this.appId,
          "resourceType": "AppConfiguration"
      }
    ];
    data['resourceType'] = 'AdditionalConfiguration';
    data['type'] = 'tags';
    data['payload'] = {tags: this.manualTags};

    return data;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
